/**
 * デバッグIssueツールの型定義
 */

/** 問題の種類 */
export type ProblemType =
  | 'error_display' // エラー表示が出る
  | 'wrong_suggestion_type' // 提案内容の種類が違う
  | 'wrong_suggestion_content' // 提案内容の中身が違う
  | 'button_not_working' // ボタンが動かない
  | 'ui_broken' // UI崩れ
  | 'timeout' // タイムアウト
  | 'other'; // その他

/** 発生率 */
export type OccurrenceRate =
  | 'reproducible' // ほぼ再現可能
  | 'half' // 50%程度
  | 'rare'; // たまに（10%以下）

/** AI機能の種類 */
export type AIFeatureType =
  | 'page_chat' // ページチャット（右パネル）
  | 'canvas_chat' // キャンバスチャット
  | 'mermaid' // mermaid
  | 'other'; // その他

/** ユーザー入力データ */
export interface UserInputData {
  title: string;
  problemTypes: ProblemType[];
  problemDetail: string;
  expectedBehavior: string;
  actualBehavior: string;
  occurrenceRate: OccurrenceRate;
  additionalInfo: string;
}

/** チャットメッセージ（シンプル化） */
export interface DebugChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

/** 自動収集データ */
export interface AutoCollectedData {
  // 基本情報
  timestamp: string;
  url: string;
  environment: 'production' | 'staging' | 'sandbox' | 'development' | 'unknown';
  browser: string;
  screenSize: {
    width: number;
    height: number;
  };
  viewportSize: {
    width: number;
    height: number;
  };

  // AI機能情報
  aiFeature: AIFeatureType;
  selectedSources: {
    type: 'page' | 'pdf' | 'none';
    name?: string;
    id?: string;
  }[];

  // チャット履歴
  chatHistory: DebugChatMessage[];
  lastUserInput?: string;

  // アプリ状態
  appState: {
    selectedPageId: string | null;
    selectedPageTitle: string | null;
    isAIGenerating: boolean;
    wsProgress: number | undefined;
    wsStatus: boolean | undefined;
    isDirty: boolean;
    selectionContext: string | null;
  };

  // エラー情報
  consoleErrors: ConsoleErrorEntry[];
}

/** コンソールエラーエントリ */
export interface ConsoleErrorEntry {
  level: 'error' | 'warn';
  message: string;
  timestamp: string;
  stack?: string;
}

/** スクリーンショットデータ */
export interface ScreenshotData {
  dataUrl: string; // base64 data URL
  uploadedUrl?: string; // S3にアップロード後のURL
}

/** Issue作成用の完全なデータ */
export interface DebugIssueData {
  userInput: UserInputData;
  autoCollected: AutoCollectedData;
  screenshot?: ScreenshotData;
}

// --- 簡素化バグ報告フォーム用の型 ---

/** 簡素化されたバグ報告の入力データ */
export interface SimplifiedBugReportInput {
  /** 何が嫌だったか（必須） */
  complaint: string;
  /** どうなって欲しかったか（オプション） */
  expectedBehavior?: string;
  /** 再現手順（オプション） */
  stepsToReproduce?: string;
  /** 環境情報（オプション） */
  environmentInfo?: string;
}

/** AI 整形結果のセクション */
export interface AiFormattedSection {
  heading: string;
  content: string;
  /** AI が推測で補完した内容かどうか */
  isInferred: boolean;
}

/** AI 補完提案 */
export interface AiSuggestion {
  id: string;
  category: 'stepsToReproduce' | 'expectedBehavior' | 'impact' | 'environment' | 'workaround';
  content: string;
  reason: string;
  /** ユーザーによる採否（UI で管理） */
  accepted?: boolean;
}

/** AI 整形結果 */
export interface AiFormatResult {
  title: string;
  sections: AiFormattedSection[];
  suggestions: AiSuggestion[];
}

/** 類似 Issue */
export interface SimilarIssue {
  number: number;
  title: string;
  url: string;
  state: 'open' | 'closed';
}

/** 問題タイプのラベル */
export const PROBLEM_TYPE_LABELS: Record<ProblemType, string> = {
  error_display: 'エラー表示が出る',
  wrong_suggestion_type: '提案内容の種類が意図したものでない',
  wrong_suggestion_content: '提案内容の中身が意図したものでない',
  button_not_working: '提案関連ボタンが動かない',
  ui_broken: 'UI崩れ',
  timeout: 'レスポンスが返ってこない／タイムアウト',
  other: 'その他',
};

/** 発生率のラベル */
export const OCCURRENCE_RATE_LABELS: Record<OccurrenceRate, string> = {
  reproducible: '発生させるための操作がほぼ明確にわかっている',
  half: '発生する時もあるししない時もあった（50%程度）',
  rare: '大体はうまくいくがたまになる（10%以下）',
};

/** AI機能のラベル */
export const AI_FEATURE_LABELS: Record<AIFeatureType, string> = {
  page_chat: 'ページアクション（右パネル）',
  canvas_chat: 'キャンバスアクション',
  mermaid: 'mermaid',
  other: 'その他',
};
